import type { UiKey } from "../i18n/ui";
import { EC2_SKUS, RDS_SKUS } from "../lib/addonInfra";
import type {
  BackendMode,
  DatabaseMode,
  DeploymentStackConfig,
  FrontendMode,
  NetworkExposure,
} from "../lib/deploymentStack";

type Props = {
  t: (key: UiKey) => string;
  value: DeploymentStackConfig;
  onChange: (next: DeploymentStackConfig) => void;
};

export function DeploymentStackPanel({ t, value, onChange }: Props) {
  function patch<K extends keyof DeploymentStackConfig>(
    key: K,
    v: DeploymentStackConfig[K],
  ) {
    onChange({ ...value, [key]: v });
  }

  const showRds = value.database === "rds" || value.database === "dynamo_and_rds";
  const showEc2Backend = value.backend === "ec2";

  return (
    <div className="stack">
      <h2>{t("stack.title")}</h2>
      <p className="hint">{t("stack.hint")}</p>

      <div className="rows">
        <label className="field">
          <span className="lab">{t("stack.frontend")}</span>
          <select
            value={value.frontend}
            onChange={(e) => patch("frontend", e.target.value as FrontendMode)}
          >
            <option value="apigw">{t("stack.frontend.apigw")}</option>
            <option value="cloudfront">{t("stack.frontend.cloudfront")}</option>
            <option value="ec2_spa">{t("stack.frontend.ec2_spa")}</option>
          </select>
        </label>

        <label className="field">
          <span className="lab">{t("stack.backend")}</span>
          <select
            value={value.backend}
            onChange={(e) => patch("backend", e.target.value as BackendMode)}
          >
            <option value="lambda">{t("stack.backend.lambda")}</option>
            <option value="ec2">{t("stack.backend.ec2")}</option>
            <option value="eks">{t("stack.backend.eks")}</option>
          </select>
        </label>

        <label className="field">
          <span className="lab">{t("stack.database")}</span>
          <select
            value={value.database}
            onChange={(e) => patch("database", e.target.value as DatabaseMode)}
          >
            <option value="dynamo">{t("stack.database.dynamo")}</option>
            <option value="rds">{t("stack.database.rds")}</option>
            <option value="dynamo_and_rds">{t("stack.database.both")}</option>
          </select>
        </label>

        <label className="field">
          <span className="lab">{t("stack.network")}</span>
          <select
            value={value.networkExposure}
            onChange={(e) =>
              patch("networkExposure", e.target.value as NetworkExposure)
            }
          >
            <option value="public">{t("stack.network.public")}</option>
            <option value="private">{t("stack.network.private")}</option>
          </select>
          <span className="mini">{t("stack.networkHelp")}</span>
        </label>
      </div>

      <div className="subgrid stack-extras">
        <p className="extras-title">{t("stack.extrasTitle")}</p>
        <label className="check">
          <input
            type="checkbox"
            checked={value.route53Enabled}
            onChange={(e) => patch("route53Enabled", e.target.checked)}
          />
          <span>{t("stack.route53")}</span>
        </label>
        <span className="mini inline">{t("stack.route53Help")}</span>
        <label className="check">
          <input
            type="checkbox"
            checked={value.albWafEnabled}
            onChange={(e) => patch("albWafEnabled", e.target.checked)}
          />
          <span>{t("stack.albWaf")}</span>
        </label>
        <span className="mini inline">{t("stack.albWafHelp")}</span>
        <label className="check">
          <input
            type="checkbox"
            checked={value.guardDutyEnabled}
            onChange={(e) => patch("guardDutyEnabled", e.target.checked)}
          />
          <span>{t("stack.guardDuty")}</span>
        </label>
        <span className="mini inline">{t("stack.guardDutyHelp")}</span>
      </div>

      {showEc2Backend && (
        <div className="subgrid">
          <label className="check">
            <input
              type="checkbox"
              checked={value.ec2AppAutoCount}
              onChange={(e) => patch("ec2AppAutoCount", e.target.checked)}
            />
            <span>{t("stack.ec2AutoCount")}</span>
          </label>
          <label className="field">
            <span className="lab">{t("stack.ec2Class")}</span>
            <select
              value={value.ec2AppSku}
              onChange={(e) =>
                patch("ec2AppSku", e.target.value as DeploymentStackConfig["ec2AppSku"])
              }
            >
              {EC2_SKUS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.id}
                </option>
              ))}
            </select>
          </label>
          {!value.ec2AppAutoCount && (
            <label className="field">
              <span className="lab">{t("stack.ec2Count")}</span>
              <input
                type="number"
                min={1}
                max={48}
                value={value.ec2AppCount}
                onChange={(e) =>
                  patch(
                    "ec2AppCount",
                    Math.min(48, Math.max(1, Math.round(Number(e.target.value) || 1))),
                  )
                }
              />
            </label>
          )}
        </div>
      )}

      {showRds && (
        <div className="subgrid">
          <label className="check">
            <input
              type="checkbox"
              checked={value.rdsAutoSize}
              onChange={(e) => patch("rdsAutoSize", e.target.checked)}
            />
            <span>{t("stack.rdsAutoSize")}</span>
          </label>
          {!value.rdsAutoSize && (
            <label className="field">
              <span className="lab">{t("stack.rdsClass")}</span>
              <select
                value={value.rdsSku}
                onChange={(e) =>
                  patch("rdsSku", e.target.value as DeploymentStackConfig["rdsSku"])
                }
              >
                {RDS_SKUS.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.id}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span className="lab">{t("stack.rdsStorage")}</span>
            <input
              type="number"
              min={20}
              max={2000}
              step={10}
              value={value.rdsStorageGb}
              onChange={(e) =>
                patch(
                  "rdsStorageGb",
                  Math.min(
                    2000,
                    Math.max(20, Math.round(Number(e.target.value) || 20)),
                  ),
                )
              }
            />
          </label>
          <label className="check">
            <input
              type="checkbox"
              checked={value.rdsMultiAz}
              onChange={(e) => patch("rdsMultiAz", e.target.checked)}
            />
            <span>{t("stack.rdsMultiAz")}</span>
          </label>
        </div>
      )}

      <style>{`
        .stack h2 {
          font-size: 1rem;
          margin: 0 0 0.35rem;
        }
        .hint {
          margin: 0 0 1rem;
          font-size: 0.82rem;
          color: var(--muted);
          line-height: 1.45;
        }
        .rows {
          display: grid;
          gap: 0.75rem;
          grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
          margin-bottom: 1rem;
        }
        .subgrid {
          padding: 0.85rem 1rem;
          border: 1px dashed var(--border);
          border-radius: 10px;
          margin-bottom: 0.75rem;
          display: grid;
          gap: 0.65rem;
        }
        .field {
          display: block;
        }
        .lab {
          display: block;
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--muted);
          margin-bottom: 0.3rem;
        }
        .field select,
        .field input[type="number"] {
          width: 100%;
          max-width: 22rem;
          padding: 0.4rem 0.5rem;
          border-radius: 8px;
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text);
          font-family: var(--font);
          font-size: 0.88rem;
        }
        .check {
          display: flex;
          align-items: center;
          gap: 0.45rem;
          font-size: 0.86rem;
          cursor: pointer;
        }
        .check input {
          accent-color: var(--accent);
        }
        .mini {
          display: block;
          margin-top: 0.35rem;
          font-size: 0.75rem;
          color: var(--muted);
          line-height: 1.4;
          max-width: 28rem;
        }
        .mini.inline {
          margin: -0.35rem 0 0.4rem 1.65rem;
        }
        .stack-extras .extras-title {
          grid-column: 1 / -1;
          margin: 0;
          font-size: 0.78rem;
          font-weight: 600;
          color: var(--muted);
        }
      `}</style>
    </div>
  );
}
